import { Flex, Text } from '@chakra-ui/react';
import {
  ButtonText,
  FileUpload,
  FormFeedback,
  TextArea,
  useToast
} from '@morressier/alembic';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { FieldLabel } from 'src/components/Field';
import { Layout } from 'src/components/Layout';
import StepHeader from 'src/pages/Organization/public/Journal/SubmissionUpload/components/StepHeader';
import { FileUploadType, IInviteExpertsForm } from './commonTypes';
import { DropzoneOptions, ErrorCode } from 'react-dropzone';
import { getFileExtension, parseUploadError } from 'src/utils/file';
import { AxiosProgressEvent } from 'axios';
import {
  deleteUploadedFile,
  getSignedPrivateAccessUrl,
  getSignedPrivateUploadUrl
} from 'src/api/queries';
import axiosInstance from 'src/api/client';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { MAX_FILE_SIZE_MB } from 'src/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile } from '@fortawesome/pro-regular-svg-icons';

interface Props {
  currentStep: number;
  totalSteps: number;
}

const MAX_FILES_AT_ONCE = 15;

const AddContextToInvitation = ({ currentStep, totalSteps }: Props) => {
  const toast = useToast();
  const {
    control,
    register,
    formState: { errors },
    watch,
    setError,
    clearErrors
  } = useFormContext<IInviteExpertsForm>();

  const { append, remove, update, fields } = useFieldArray({
    control,
    name: 'addContext.attachments'
  });
  const { submissionId } = useSubmissionData();

  const getUploadUrl = (extension: string) =>
    getSignedPrivateUploadUrl(submissionId, extension);

  const uploadFile = async (file: File, index: number) => {
    const fileExtension = getFileExtension(file.name);
    let fileToUpload: FileUploadType = {
      name: file.name,
      referenceName: file.name,
      progress: 0,
      isUploading: true,
      extension: fileExtension,
      url: ''
    };

    const onUploadProgress = () => (progressEvent: AxiosProgressEvent) => {
      const { loaded, total = Number.POSITIVE_INFINITY } = progressEvent;
      const progress = Math.floor((loaded * 100) / total);

      fileToUpload = {
        ...fileToUpload,
        progress
      };

      update(index, fileToUpload);
    };

    try {
      append(fileToUpload);

      const uploadParams = await getUploadUrl(fileExtension);
      if (!uploadParams) return;

      const formData = new FormData();
      formData.append('file', file, file.name);

      const {
        data: { id, file_url: url, original_filename }
      } = await axiosInstance.put<{
        id: string;
        file_url: string;
        original_filename: string;
      }>(uploadParams.signed_url, formData, {
        onUploadProgress: onUploadProgress()
      });

      fileToUpload = {
        ...fileToUpload,
        fileId: id,
        url,
        name: original_filename,
        referenceName: original_filename,
        extension: fileExtension
      };

      update(index, fileToUpload);
    } catch (e) {
      setError(`addContext.attachments.${index}`, {
        type: 'upload-error',
        message: parseUploadError(e)
      });
    } finally {
      update(index, {
        ...fileToUpload,
        isUploading: false
      });
    }
  };
  const onDrop: DropzoneOptions['onDrop'] = async (acceptedFiles: File[]) => {
    clearErrors('addContext.attachments');
    const accepted = acceptedFiles;
    await Promise.all(
      accepted.map((file, index) => uploadFile(file, index + fields.length))
    );
  };

  const onDropRejected: DropzoneOptions['onDropRejected'] =
    async rejectedFiles => {
      if (!rejectedFiles || rejectedFiles.length === 0) return;

      // Separate rejected files into 'too many files' and other errors
      const tooManyFiles = rejectedFiles.filter(r =>
        r.errors.some(e => e.code === 'too-many-files')
      );
      const otherRejected = rejectedFiles.filter(
        r => !r.errors.some(e => e.code === 'too-many-files')
      );

      // Upload only allowed number of files from 'too many files'
      if (tooManyFiles.length > 0 && MAX_FILES_AT_ONCE > 0) {
        const filesToUpload = tooManyFiles
          .slice(0, MAX_FILES_AT_ONCE)
          .map(r => r.file);
        toast({
          variant: 'attention',
          title: 'File Upload Limit Exceeded',
          description:
            'You can only upload up to ' +
            MAX_FILES_AT_ONCE +
            ' files at once. Excess files were not uploaded.',
          isClosable: true
        });

        await Promise.all(
          filesToUpload.map((file, index) =>
            uploadFile(file, index + fields.length)
          )
        );
      }

      // Handle other rejected files (wrong type, too large, etc.)
      if (otherRejected[0]) {
        const rejectedFileError = otherRejected[0].errors[0];
        if (rejectedFileError?.code === 'file-invalid-type') {
          setError('addContext.attachments', {
            type: rejectedFileError.code,
            message: 'An unsupported file format selected.'
          });
        }
        if (
          rejectedFileError?.code === ErrorCode.FileTooLarge ||
          rejectedFileError?.code === ErrorCode.TooManyFiles
        ) {
          setError('addContext.attachments', {
            type: rejectedFileError.code,
            message: rejectedFileError.message
          });
        }
      }
    };
  const removeFile = async (index: number, fileId?: string) => {
    remove(index);
    fileId && deleteUploadedFile(fileId);
  };

  const handleOpenFile = async (url: string) => {
    if (!submissionId) {
      return;
    }
    const signedUrl = await getSignedPrivateAccessUrl(submissionId, url);
    window.open(signedUrl, '_blank');
  };

  return (
    <Layout width="960px">
      <Flex flexDirection="column" gap={8}>
        <StepHeader
          currentStep={currentStep}
          totalSteps={totalSteps}
          heading="Add Context to Invitation"
          description="Provide guidance to experts by sharing your feedback along with any relevant details that can assist them in understanding the context of their review."
          marginY={0}
        />
        <Flex
          gap={4}
          padding={6}
          backgroundColor="surfacePrimaryFill"
          flexDirection="column"
        >
          <Flex flexDirection="column" gap={2}>
            <FieldLabel
              label="Task to Perform"
              subText="Leave a note for this contact with instructions and any details to help them complete their task."
              isRequired
            />
            <TextArea
              placeholder="Describe the task to perform"
              {...register('addContext.comment')}
              value={watch('addContext.comment')}
              isInvalid={Boolean(errors?.addContext?.comment)}
              errorMessage={errors?.addContext?.comment?.message}
            />
          </Flex>
          <Flex flexDirection="column" gap={2}>
            <FieldLabel
              label="Attach Files"
              subText="Attach files to support your invitation if needed."
              isRequired={false}
            />
            <FileUpload
              {...register('addContext.attachments')}
              uploadButtonText="Select files"
              multiple
              onDrop={onDrop}
              onDropRejected={onDropRejected}
              maxFiles={MAX_FILES_AT_ONCE}
              maxSize={MAX_FILE_SIZE_MB * 1000000}
              isInvalid={Boolean(errors?.addContext?.attachments)}
            />
            {errors?.addContext?.attachments && (
              <FormFeedback variant="error">
                {errors?.addContext?.attachments?.message}
              </FormFeedback>
            )}
          </Flex>
          {fields.length ? (
            <Flex flexDirection="column">
              {fields.map((file, index) => (
                <Flex
                  key={file.id || file.url}
                  flexDirection="row"
                  justifyContent="space-between"
                  padding={4}
                  bgColor="surfacePrimaryFill"
                  borderBottom="1px"
                  borderColor="neutralPrimaryBorder"
                  _last={{ borderBottom: 'none' }}
                  width="100%"
                >
                  <Flex alignItems="center" gap={2} flexDirection="row">
                    <FontAwesomeIcon icon={faFile} fontSize={20} />
                    <a
                      onClick={() => handleOpenFile(file.url)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Text textStyle="textBody">{file.name}</Text>
                    </a>
                  </Flex>
                  {file.isUploading ? (
                    <Text textStyle="textContext">Uploading</Text>
                  ) : (
                    <>
                      {errors?.addContext?.attachments?.[index] && (
                        <FormFeedback variant="error">
                          {errors?.addContext?.attachments?.[index]?.message}
                        </FormFeedback>
                      )}
                      <ButtonText
                        onClick={() => removeFile(index, file.fileId)}
                      >
                        Delete
                      </ButtonText>
                    </>
                  )}
                </Flex>
              ))}
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    </Layout>
  );
};

export default AddContextToInvitation;
